import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GuardedFormStepper, useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { InstitutionOnboardingProgressStep, useProgress } from '@/context/progress';
import {
  INSTITUTION_AML_CTF_SANCTIONS_DEFAULTS,
  type InstitutionAmlCtfSanctions,
  institutionAmlCtfSanctionsPoliciesScreeningAndMonitoringSchema,
  institutionAmlCtfSanctionsProhibitedRelationshipsAndGovernanceSchema,
  institutionAmlCtfSanctionsToInstitutionDetailsAmlCtfSanctionsApi,
} from '@/features/institution/aml-ctf-sanctions/schema';
import { InstitutionGoToReviewStepperButton } from '@/features/institution/components/go-to-review-button';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { useInstitutionDetailsMutation, useInstitutionDetailsV2Answers, useInstitutionDetailsVersion } from '@/features/institution/hooks/useInstitutionDetails';
import { getDetailsKeyProgress, INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import type { FormStep } from '@/types/components';
import { InstitutionAmlCtfSanctionsFieldsetGovernance } from './fieldsets/governance';
import { InstitutionAmlCtfSanctionsFieldsetMonitoring } from './fieldsets/monitoring';
import { InstitutionAmlCtfSanctionsFieldsetPolicies } from './fieldsets/policies';
import { InstitutionAmlCtfSanctionsFieldsetProhibitedRelationships } from './fieldsets/prohibited-relationships';
import { InstitutionAmlCtfSanctionsFieldsetScreening } from './fieldsets/screening';

const InstitutionAmlCtfSanctionsFormStepPoliciesScreeningAndMonitoringLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.amlCtfSanctions.steps.policiesScreeningAndMonitoring.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.amlCtfSanctions.steps.policiesScreeningAndMonitoring.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionAmlCtfSanctionsFormStepPoliciesScreeningAndMonitoringLegend.displayName = 'InstitutionAmlCtfSanctionsFormStepPoliciesScreeningAndMonitoringLegend';

const InstitutionAmlCtfSanctionsFormStepPoliciesScreeningAndMonitoring = () => (
  <div className="space-y-6">
    <InstitutionAmlCtfSanctionsFieldsetPolicies />
    <InstitutionAmlCtfSanctionsFieldsetScreening />
    <InstitutionAmlCtfSanctionsFieldsetMonitoring />
  </div>
);
InstitutionAmlCtfSanctionsFormStepPoliciesScreeningAndMonitoring.displayName = 'InstitutionAmlCtfSanctionsFormStepPoliciesScreeningAndMonitoring';

const InstitutionAmlCtfSanctionsFormStepProhibitedRelationshipsAndGovernanceLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.amlCtfSanctions.steps.prohibitedRelationshipsAndGovernance.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.amlCtfSanctions.steps.prohibitedRelationshipsAndGovernance.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionAmlCtfSanctionsFormStepProhibitedRelationshipsAndGovernanceLegend.displayName = 'InstitutionAmlCtfSanctionsFormStepProhibitedRelationshipsAndGovernanceLegend';

const InstitutionAmlCtfSanctionsFormStepProhibitedRelationshipsAndGovernance = () => (
  <div className="space-y-6">
    <InstitutionAmlCtfSanctionsFieldsetProhibitedRelationships />
    <InstitutionAmlCtfSanctionsFieldsetGovernance />
  </div>
);
InstitutionAmlCtfSanctionsFormStepProhibitedRelationshipsAndGovernance.displayName = 'InstitutionAmlCtfSanctionsFormStepProhibitedRelationshipsAndGovernance';

const FORM_STEPS = {
  POLICIES_SCREENING_AND_MONITORING: {
    Component: InstitutionAmlCtfSanctionsFormStepPoliciesScreeningAndMonitoring,
    Legend: InstitutionAmlCtfSanctionsFormStepPoliciesScreeningAndMonitoringLegend,
    schema: institutionAmlCtfSanctionsPoliciesScreeningAndMonitoringSchema,
  },
  PROHIBITED_RELATIONSHIPS_AND_GOVERNANCE: {
    Component: InstitutionAmlCtfSanctionsFormStepProhibitedRelationshipsAndGovernance,
    Legend: InstitutionAmlCtfSanctionsFormStepProhibitedRelationshipsAndGovernanceLegend,
    schema: institutionAmlCtfSanctionsProhibitedRelationshipsAndGovernanceSchema,
  },
} satisfies Record<string, FormStep<InstitutionAmlCtfSanctions>>;

const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
const STEP_LEGENDS = Object.values(FORM_STEPS).map((step) => step.Legend);
const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);

const InstitutionAmlCtfSanctionsFormCurrentStep = () => {
  const { currentStep, totalSteps } = useGuardedFormStepper<InstitutionAmlCtfSanctions>();
  const { data: version } = useInstitutionDetailsVersion();
  const { setCurrentStep } = useProgress();
  const Step = STEPS[currentStep];

  useEffect(() => {
    if (!version) return;

    return setCurrentStep(
      InstitutionOnboardingProgressStep.INSTITUTION_INFORMATION,
      getDetailsKeyProgress(INSTITUTION_DETAILS_TYPE.ONBOARDING, version, 'amlCtfSanctions', currentStep, totalSteps),
    );
  }, [currentStep, setCurrentStep, totalSteps, version]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll when currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!Step) throw new Error('Step not found');

  return (
    <PageCardContent className="pt-8">
      <div className="animate-in fade-in" key={currentStep}>
        <Step />
      </div>
    </PageCardContent>
  );
};
InstitutionAmlCtfSanctionsFormCurrentStep.displayName = 'InstitutionAmlCtfSanctionsFormCurrentStep';

const InstitutionAmlCtfSanctionsFormCurrentStepLegend = () => {
  const { currentStep } = useGuardedFormStepper<InstitutionAmlCtfSanctions>();
  const Legend = STEP_LEGENDS[currentStep];

  if (!Legend) throw new Error('Legend not found');

  return <Legend />;
};
InstitutionAmlCtfSanctionsFormCurrentStepLegend.displayName = 'InstitutionAmlCtfSanctionsFormCurrentStepLegend';

const InstitutionAmlCtfSanctionsFormBackButton = () => {
  const { currentStep, prevStep } = useGuardedFormStepper<InstitutionAmlCtfSanctions>();
  const navigate = useNavigate();
  const onboardingPath = useInstitutionOnboardingBasePath();

  const handlePrevStep = useCallback(() => {
    if (currentStep === 0) {
      navigate(`${onboardingPath}/regulatory-information`, { replace: true });
    } else {
      prevStep();
    }
  }, [currentStep, prevStep, navigate, onboardingPath]);

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
InstitutionAmlCtfSanctionsFormBackButton.displayName = 'InstitutionAmlCtfSanctionsFormBackButton';

const InstitutionAmlCtfSanctionsFormFooter = ({ isPending }: { isPending: boolean }) => {
  const { currentStep, nextStep } = useGuardedFormStepper<InstitutionAmlCtfSanctions>();
  const { t } = useTranslation();
  const Step = STEPS[currentStep];

  if (Step && 'isAutoNextStep' in Step && Step.isAutoNextStep) return null;

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={() => nextStep()}>
        <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
InstitutionAmlCtfSanctionsFormFooter.displayName = 'InstitutionAmlCtfSanctionsFormFooter';

const InstitutionAmlCtfSanctionsForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'amlCtfSanctions');
  const { isPending, mutateAsync } = useInstitutionDetailsMutation();
  const navigate = useNavigate();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  const onSubmit = async (data: InstitutionAmlCtfSanctions, { isReturningToReview = false }: { isReturningToReview?: boolean } = {}) => {
    await mutateAsync(
      { amlCtfSanctions: await institutionAmlCtfSanctionsToInstitutionDetailsAmlCtfSanctionsApi(data) },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          navigate(isReturningToReview ? `${onboardingPath}/review` : `${onboardingPath}/compliance-training`, { replace: true });
        },
      },
    );
  };

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.amlCtfSanctions ?? INSTITUTION_AML_CTF_SANCTIONS_DEFAULTS;

  return (
    <GuardedFormStepper className="relative" defaultValues={defaultValues} steps={STEPS} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <fieldset>
        <InstitutionAmlCtfSanctionsFormCurrentStepLegend />
        <div className="flex flex-row justify-between -md:justify-end">
          <InstitutionAmlCtfSanctionsFormBackButton />
          <InstitutionGoToReviewStepperButton onSubmit={(data: InstitutionAmlCtfSanctions) => onSubmit(data, { isReturningToReview: true })} />
        </div>
        <PageCard size="lg" bordered fixedFooter>
          <InstitutionAmlCtfSanctionsFormCurrentStep />
          <InstitutionAmlCtfSanctionsFormFooter isPending={isPending} />
        </PageCard>
      </fieldset>
    </GuardedFormStepper>
  );
};
InstitutionAmlCtfSanctionsForm.displayName = 'InstitutionAmlCtfSanctionsForm';

export default InstitutionAmlCtfSanctionsForm;
